import type { NavGroup, NavItem } from './nav';

/**
 * Nav entries the mockup could not express, added beside the generated NAV rather than into it — nav.ts is written by
 * tools/mockup-extract and is never hand-edited.
 *
 * The mockup drew only the stock-count wizard, so "Stock count" points at /inv/count and the register at /inv/counts —
 * a finished list with filters, paging and a status column — was reachable from nowhere at all. Every other area puts
 * its register in the nav and opens the document from a row; counts now do the same. The mockup's own entry is left
 * exactly as drawn, so this adds a way in without changing what was signed off.
 */
export const NAV_EXTRA: Readonly<Record<string, { after: string; item: NavItem }[]>> = {
  // The process flow is the landing page; it is named in the nav too, because people want a way back to it.
  dash: [
    { after: '/dash/stores', item: { label: 'The whole process', route: '/flow' } },
    // Help sits with the overview rather than under Administration: it is for everyone, and a storekeeper never opens
    // the Administration group at all (ADR-017). It follows the last of the boards — an extra can only be slotted in
    // after an item the generated nav actually holds, so anchoring it to /flow (an extra itself) would drop it.
    { after: '/dash/mgmt', item: { label: 'Help & training', route: '/help' } },
  ],
  inv: [
    // ADPAD asked "where is material request and issue?". The chain has always been there — an engineer raises an issue
    // note, the matrix sends a repairable to the Head of Tech. Stores, the storekeeper posts it — but nothing in the nav
    // was called what the department calls it, so nobody found the way in. The mockup's own labels ("Issue counter
    // (scan)", "Issue notes") stay exactly as drawn; this adds the door an engineer looks for.
    { after: '/inv/counter', item: { label: 'Material request', route: '/inv/issue' } },
    { after: '/inv/count', item: { label: 'Stock counts', route: '/inv/counts' } },
  ],
  // The monitors answer fixed questions; this answers the reader's own (ADR-021). It follows the last monitor,
  // because an extra can only be slotted after an item the generated nav actually holds.
  mon: [{ after: '/mon/alerts', item: { label: 'Reports', route: '/rpt/reports' } }],
  admin: [{ after: '/admin/settings', item: { label: 'Themes', route: '/admin/themes' } }],
};

/** The generated nav with the extra entries slotted in after the item each one follows. */
export function withExtraNav(nav: readonly NavGroup[]): NavGroup[] {
  return nav.map((group) => {
    const extras = NAV_EXTRA[group.key];
    if (!extras) return { ...group };
    const items: NavItem[] = [];
    for (const item of group.items) {
      items.push(item);
      for (const extra of extras) {
        if (extra.after === item.route && !group.items.some((i) => i.route === extra.item.route)) items.push(extra.item);
      }
    }
    return { ...group, items };
  });
}
